import { OdinIcon } from '@myosh/odin-components';
import { useTranslation } from 'react-i18next';
import { useFileDownload } from '../../../../hooks/use-file-download';
import clsx from 'clsx';
import type { MouseEvent } from 'react';

export interface ThumbnailActionsProps {
  id: string;
  name: string;
  downloadUrl: string;
  previewUrl?: string;
  readOnly: boolean;
  attachmentId?: string;
  onDelete: (id: string) => void;
}

export const ThumbnailActions = ({
  id,
  name,
  downloadUrl,
  previewUrl,
  readOnly,
  attachmentId,
  onDelete,
}: ThumbnailActionsProps) => {
  const { t } = useTranslation();
  const { handleDownload, isDownloading } = useFileDownload({
    fileId: id,
    fileName: name,
    currentUrl: downloadUrl,
  });

  const actionStyles = 'hidden group-hover:inline-flex absolute top-0 right-0 py-1 w-full justify-end bg-gray-5';
  const actionStyle = clsx('inline-flex cursor-pointer ml-1', { 'pointer-events-none opacity-60': isDownloading });

  const shouldShowPreviewButton = previewUrl && window.ReactNativeWebView === undefined;

  const handleDownloadWithStopPropagation = async (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();
    await handleDownload();
  };

  return (
    <div className={actionStyles}>
      {!readOnly && (
        // TQ attachments should be deleted using the 'attachmentId' property
        <a title={t('delete')} role="button" className={actionStyle} onClick={() => onDelete(attachmentId ?? id)}>
          <OdinIcon icon="Trash" />
        </a>
      )}
      <a
        title={t('download')}
        className={actionStyle}
        download={name}
        href={isDownloading ? undefined : downloadUrl}
        onClick={handleDownloadWithStopPropagation}
        aria-disabled={isDownloading}
        tabIndex={isDownloading ? -1 : 0}
      >
        <OdinIcon icon="Download" />
      </a>
      {shouldShowPreviewButton && (
        <a
          title={t('preview')}
          className={actionStyle}
          href={previewUrl}
          target="_blank"
          rel="noreferrer"
          onClick={(event) => event.stopPropagation()}
        >
          <OdinIcon icon="ExternalLink" />
        </a>
      )}
    </div>
  );
};
